import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  InputBase,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as BackIcon,
  ArrowForward as ArrowForwardIcon,
  People as PeopleIcon,
  Badge as BadgeIcon,
  Add as PlusIcon,
  Remove as MinusIcon,
} from '@mui/icons-material';

/* -------------------- Design tokens -------------------- */
const teal = { 50: '#f0fdfa', 200: '#99f6e4', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59', 900: '#134e4a' };
const neutral = { 50: '#fafafa', 200: '#e4e4e7', 500: '#71717a', 600: '#52525b', 700: '#3f3f46', 800: '#27272a', 900: '#18181b' };
const BASE_VOICE_CREDITS_PER_USER_PER_MONTH = 1000;

/**
 * Add User Seats Modal - Two-step flow (prorated for remaining billing days)
 *
 * All calculations use effective cost and effective credits from API:
 * - Cost: effective_per_user_cost × seats (e.g. 11,933.33 × 2)
 * - Credits: effective_voice_credits_per_cycle × seats (e.g. 5,967 × 2 = 11,934)
 */
const ProfarmaInvoiceModalAddUser = ({ open, onClose, unitsData, data, loading, error, paymentUrl, onConfirm, confirmLoading, confirmError, onPayNow, onNext, onBack }) => {
  const formatCurrency = (amount, currency = 'INR') => {
    const symbol = currency === 'INR' ? '₹' : '$';
    return `${symbol}${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  // Same decimal rules for credits (integer display; backend may send float)
  const formatCredits = (value) => {
    const n = Number(value);
    return Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  };

  // API returns { success, action, units }. Extract units safely (no .data in response).
  const units = unitsData?.units ?? unitsData?.data?.units ?? {};
  const lineItems = data?.particulars || data?.line_items || [];
  const hasProformaData = lineItems.length > 0;
  const currentSlotsFromProforma = lineItems[0]?.quantity ?? 1;

  const remainingSeats = units.remaining_user_seat ?? units.remaining_seats ?? 0;
  const remainingDays = units.remaining_days ?? null;

  // Use effective cost/credits for all calculations (prorated for remaining days)
  const effectivePerUserCost = Number(units.effective_per_user_cost ?? units.per_user_cost ?? 0);
  const perUserCostMonthly = Number(units.per_user_cost ?? 0); // reference only (e.g. ₹2,000 / mo in plan label)
  const billingCycleMonths = Number(units.billing_cycle_months ?? 1);
  const effectiveCreditsPerUserPerCycle = Number(units.effective_voice_credits_per_cycle ?? units.base_voice_credits_per_cycle ?? BASE_VOICE_CREDITS_PER_USER_PER_MONTH);

  const planLabel = units.plan_name ? `${String(units.plan_name).toUpperCase()} PLAN` : 'STANDARD PLAN';

  const taxRate = Number(data?.tax_rate ?? units.tax_rate ?? 0.18) || 0.18;
  const currency = data?.currency ?? units.currency ?? 'INR';
  const cannotPurchaseSeats = remainingDays !== null && remainingDays <= 0;

  // Min/max limits for seats
  const MIN_SEATS = 1;
  const MAX_SEATS = 9999;

  const [slotsInput, setSlotsInput] = useState('1');
  const [step, setStep] = useState(1);
  const [pendingStep2, setPendingStep2] = useState(false);

  useEffect(() => { if (open) { setSlotsInput('1'); setStep(1); setPendingStep2(false); } }, [open]);
  useEffect(() => { if (open && step === 2 && hasProformaData) setSlotsInput(String(currentSlotsFromProforma)); }, [open, step, hasProformaData, currentSlotsFromProforma]);
  useEffect(() => { if (error && pendingStep2) setPendingStep2(false); }, [error, pendingStep2]);

  // Enforce min/max limits on slots quantity
  const slotsQty = Math.max(MIN_SEATS, Math.min(MAX_SEATS, parseInt(slotsInput, 10) || MIN_SEATS));

  // All cost: effective_per_user_cost × seats
  const subtotalFromFormula = effectivePerUserCost * slotsQty;
  const subtotalDisplay = step === 2 && data?.subtotal != null ? Number(data.subtotal) : subtotalFromFormula;
  const totalDisplay = step === 2 && data?.total != null ? Number(data.total) : subtotalDisplay + (subtotalDisplay * taxRate);
  const backendTax = step === 2 && (data?.tax_amount != null || data?.tax != null) ? Number(data.tax_amount ?? data.tax ?? 0) : null;
  const taxDisplay = step === 2 && data?.total != null && data?.subtotal != null && (backendTax == null || backendTax === 0)
    ? (Number(data.total) - Number(data.subtotal))
    : (backendTax != null && backendTax > 0 ? backendTax : subtotalDisplay * taxRate);

  // Total credits: effective_voice_credits_per_cycle × seats (e.g. 5,967 × 2 = 11,934)
  const totalFreeCredits = (effectiveCreditsPerUserPerCycle != null && !Number.isNaN(effectiveCreditsPerUserPerCycle))
    ? Math.round(effectiveCreditsPerUserPerCycle * slotsQty)
    : (step === 2 && data?.free_credits_to_be_granted != null ? Number(data.free_credits_to_be_granted) : 0);

  const handleContinueToReview = () => {
    const n = parseInt(slotsInput, 10);
    const qty = Number.isNaN(n) || n < MIN_SEATS ? MIN_SEATS : Math.min(MAX_SEATS, n);
    setSlotsInput(String(qty));
    setStep(2);
    setPendingStep2(true);
    if (onNext) onNext(qty, () => setPendingStep2(false));
  };

  const handleSeatsInputBlur = () => {
    const n = parseInt(slotsInput, 10);
    const qty = Number.isNaN(n) || n < MIN_SEATS ? MIN_SEATS : Math.min(MAX_SEATS, n);
    setSlotsInput(String(qty));
  };

  const handleBack = () => { setStep(1); setPendingStep2(false); if (onBack) onBack(); };

  const hasUnitsData = !!(units.effective_per_user_cost != null || units.per_user_cost != null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: '#fff', flex: 1 }}>
        {/* Header */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: neutral[200] }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: teal[600], color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>SG</Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: neutral[900] }}>SalesGro.ai</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: neutral[500] }}>Add users to your plan</Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        </Box>

        {/* Loading state */}
        {loading && !hasUnitsData && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, py: 6 }}>
            <CircularProgress size={36} sx={{ color: teal[600] }} />
            <Typography sx={{ mt: 2, fontSize: '0.875rem', color: neutral[600], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Loading...
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {error && !hasUnitsData && (
          <Box sx={{ px: 2.5, py: 3 }}>
            <Alert severity="error" sx={{ fontSize: '0.75rem' }}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Step 1: Configure */}
        {step === 1 && hasUnitsData && (
          <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, flex: 1, overflow: 'auto' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: neutral[600], mb: 2, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              New users are charged only for the remaining days in your current billing cycle.
            </Typography>

            {/* NUMBER OF NEW SEATS */}
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: neutral[700], textTransform: 'uppercase', letterSpacing: '0.03em', mb: 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Number of new seats
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: '#fff', 
              border: '1px solid', 
              borderColor: neutral[200],
              mb: 2,
              width: 'fit-content'
            }}>
              <IconButton 
                onClick={() => setSlotsInput(String(Math.max(MIN_SEATS, slotsQty - 1)))} 
                disabled={slotsQty <= MIN_SEATS || cannotPurchaseSeats}
                sx={{ 
                  width: 40, height: 40, 
                  border: '1px solid', borderColor: neutral[200], borderRadius: '50%',
                  bgcolor: '#fff', '&:hover': { borderColor: teal[500], bgcolor: teal[50] },
                  '&:disabled': { opacity: 0.5 }
                }}
              >
                <MinusIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <InputBase
                type="number"
                inputProps={{ min: MIN_SEATS, max: MAX_SEATS, 'aria-label': 'Seats to add' }}
                value={slotsInput}
                onChange={(e) => setSlotsInput(e.target.value)}
                onBlur={handleSeatsInputBlur}
                disabled={cannotPurchaseSeats}
                sx={{
                  width: 56,
                  '& input': {
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    color: neutral[900],
                    py: 0.5,
                    border: 'none',
                    bgcolor: 'transparent',
                    '&:focus': { outline: 'none' },
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
              <IconButton 
                onClick={() => setSlotsInput(String(Math.min(MAX_SEATS, slotsQty + 1)))} 
                disabled={cannotPurchaseSeats || slotsQty >= MAX_SEATS}
                sx={{ 
                  width: 40, height: 40, 
                  border: '1px solid', borderColor: neutral[200], borderRadius: '50%',
                  bgcolor: '#fff', '&:hover': { borderColor: teal[500], bgcolor: teal[50] },
                  '&:disabled': { opacity: 0.5 }
                }}
              >
                <PlusIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* PRICE PER NEW USER & CREDITS INCLUDED cards — prorated */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: teal[50], border: '1px solid', borderColor: teal[200] }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PeopleIcon sx={{ color: teal[700], fontSize: 20 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: teal[900], textTransform: 'uppercase', letterSpacing: '0.02em', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Price per new user
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '1.375rem', fontWeight: 800, color: teal[700], mb: 0.75, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {formatCurrency(effectivePerUserCost, currency)}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: teal[700], fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.4, mb: 1 }}>
                  Prorated for {remainingDays ?? 0} remaining days in this billing cycle.
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: teal[800], textTransform: 'uppercase', letterSpacing: '0.02em', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {planLabel}: {formatCurrency(perUserCostMonthly, currency)} / mo per user
                </Typography>
              </Box>

              <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f5f3ff', border: '1px solid', borderColor: '#ddd6fe' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BadgeIcon sx={{ color: '#5b21b6', fontSize: 20 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#4c1d95', textTransform: 'uppercase', letterSpacing: '0.02em', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Credits included
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '1.375rem', fontWeight: 800, color: '#5b21b6', mb: 0.25, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {formatCredits(effectiveCreditsPerUserPerCycle)}
                  <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#6d28d9', ml: 0.5 }}>credits</Typography>
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#5b21b6', fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.4, mb: 1 }}>
                  Based on remaining {remainingDays ?? 0} days of the cycle.
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.02em', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Added instantly after payment
                </Typography>
              </Box>
            </Box>

            <Button 
              variant="contained" 
              onClick={handleContinueToReview} 
              disabled={slotsQty < 1 || cannotPurchaseSeats}
              fullWidth
              endIcon={<ArrowForwardIcon />}
              sx={{ 
                py: 1.25, 
                fontSize: '0.9375rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: teal[600], 
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                '&:hover': { bgcolor: teal[700] }
              }}
            >
              Continue to Review
            </Button>

            <Typography sx={{ fontSize: '0.65rem', color: neutral[500], textTransform: 'uppercase', letterSpacing: '0.04em', mt: 1.5, textAlign: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Charges are prorated based on remaining billing days
            </Typography>

            {cannotPurchaseSeats && (
              <Alert severity="warning" sx={{ mt: 2, fontSize: '0.75rem', py: 0.75 }}>
                No remaining days in current cycle. Seat purchase is disabled until renewal.
              </Alert>
            )}
            {error && !cannotPurchaseSeats && (
              <Alert severity="error" sx={{ mt: 2, fontSize: '0.75rem', py: 0.75 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {/* Step 2: Review & Pay — Order Summary */}
        {step === 2 && !pendingStep2 && (
          <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, flex: 1, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <IconButton onClick={handleBack} size="small" sx={{ p: 0.5 }}>
                <BackIcon sx={{ fontSize: 22, color: neutral[700] }} />
              </IconButton>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: neutral[900], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Order Summary
              </Typography>
            </Box>

            {/* Table: ITEM, QTY, UNIT PRICE (PRORATED), AMOUNT */}
            <Box sx={{ border: '1px solid', borderColor: neutral[200], borderRadius: 2, overflow: 'hidden', mb: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 60px 1fr 1fr', gap: 1, px: 1.5, py: 1, bgcolor: neutral[50], borderBottom: '1px solid', borderColor: neutral[200] }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: neutral[600], textTransform: 'uppercase', letterSpacing: '0.03em' }}>Item</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: neutral[600], textTransform: 'uppercase', letterSpacing: '0.03em' }}>Qty</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: neutral[600], textTransform: 'uppercase', letterSpacing: '0.03em' }}>Unit price (prorated)</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: neutral[600], textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'right' }}>Amount</Typography>
              </Box>
              {lineItems.map((item, idx) => {
                const desc = (item.description || '').toLowerCase();
                const isFreeCredits = desc.includes('free') && (desc.includes('credit') || desc.includes('voice'));
                const qty = isFreeCredits ? totalFreeCredits : (item.quantity ?? 0);
                const unitPrice = isFreeCredits ? 0 : (item.unit_price ?? 0);
                const amount = isFreeCredits ? 0 : (item.total_amount ?? item.amount ?? (item.quantity * (item.unit_price || 0)));
                return (
                  <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '1fr 60px 1fr 1fr', gap: 1, px: 1.5, py: 1.25, borderBottom: idx < lineItems.length - 1 ? '1px solid' : 'none', borderColor: neutral[200], alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: neutral[800], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {item.description || (isFreeCredits ? 'Free Credits' : 'New Seat')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: neutral[800], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {formatCredits(qty)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: neutral[800], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {formatCurrency(unitPrice, currency)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: isFreeCredits ? teal[600] : neutral[900], textAlign: 'right', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {formatCurrency(amount, currency)}
                    </Typography>
                  </Box>
                );
              })}
              {/* GST row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 60px 1fr 1fr', gap: 1, px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: neutral[200], alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.8125rem', color: neutral[800], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  GST ({(taxRate * 100).toFixed(0)}%)
                </Typography>
                <Box />
                <Box />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: neutral[900], textAlign: 'right', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {formatCurrency(taxDisplay, currency)}
                </Typography>
              </Box>
              {/* Total Payable Today */}
              <Box sx={{ px: 1.5, py: 1.5, bgcolor: neutral[50] }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: neutral[900], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Total Payable Today
                  </Typography>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: neutral[900], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {formatCurrency(totalDisplay, currency)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Credits to be added */}
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: teal[50], border: '1px solid', borderColor: teal[200], mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <BadgeIcon sx={{ color: teal[700], fontSize: 22 }} />
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: teal[800], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Credits to be added: {formatCredits(totalFreeCredits)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button 
                variant="outlined" 
                onClick={handleBack}
                disabled={confirmLoading}
                sx={{ 
                  flex: '0 0 auto',
                  py: 1.125, px: 2.5,
                  fontSize: '0.875rem', fontWeight: 600, textTransform: 'none', borderRadius: 2,
                  borderColor: neutral[300], color: neutral[700],
                  '&:hover': { borderColor: neutral[400], bgcolor: neutral[50] }
                }}
              >
                Back
              </Button>
              {!paymentUrl ? (
                <Button 
                  variant="contained" 
                  onClick={onConfirm}
                  disabled={confirmLoading}
                  fullWidth
                  sx={{ 
                    py: 1.125, fontSize: '0.875rem', fontWeight: 700, textTransform: 'none', borderRadius: 2,
                    bgcolor: teal[600], '&:hover': { bgcolor: teal[700] }
                  }}
                >
                  {confirmLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm Payment'}
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  onClick={onPayNow}
                  disabled={confirmLoading}
                  fullWidth
                  sx={{ 
                    py: 1.125, fontSize: '0.875rem', fontWeight: 700, textTransform: 'none', borderRadius: 2,
                    bgcolor: teal[600], '&:hover': { bgcolor: teal[700] }
                  }}
                >
                  Pay Now — {formatCurrency(totalDisplay, currency)}
                </Button>
              )}
            </Box>

            <Typography sx={{ fontSize: '0.65rem', color: neutral[500], textTransform: 'uppercase', letterSpacing: '0.04em', mt: 1.5, textAlign: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Charges are prorated based on remaining billing days
            </Typography>

            {confirmError && (
              <Alert severity="error" sx={{ mt: 2, fontSize: '0.75rem', py: 0.5 }}>
                {confirmError}
              </Alert>
            )}
          </Box>
        )}

        {/* Loading */}
        {pendingStep2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, py: 4 }}>
            <CircularProgress size={32} sx={{ color: teal[600] }} />
            <Typography sx={{ mt: 1.5, fontSize: '0.8125rem', color: neutral[600], fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Loading invoice...
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default ProfarmaInvoiceModalAddUser;
